import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useIsFetching } from 'react-query';
import moment, { Moment } from 'moment';
import lodash from 'lodash';
import debounce from 'lodash/debounce';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { ActionCommentDTO } from '@behavior/activity/dto/ActionCommentDTO';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import { ActivityInitialDataResponse } from '@components/Activity/types/ActivityInitialDataResponse';
import { getPersonAutocomplete } from '@behavior/persons/personAutocompleteApi';
import usePostComment from '@behavior/activity/usePostComment';
import { QueryKey } from '@behavior/QueryKey';
import { backendDateFormat } from '@src/Dates';
import GridColumnInputWrapper from '@components/common/GridColumnInputWrapper';
import TextArea from '@components/common/TextArea';
import { Action } from '@src/routes';
import ShareButton from '../ShareButton';

type Props = {
  onRemove?: () => void;
  actionContent: ActionDTO;
  setActionCardContent?: (actionContent: ActionDTO) => void;
  initialData?: ActivityInitialDataResponse;
  disabled?: boolean;
  viewOnly?: boolean;
  index: number;
  showPersonalData?: boolean;
};

// Mirrors the palette used by ActionDetailView + ActionsDetailsContainer.
const INK = '#111418';
const TEXT = '#1f2328';
const TEXT_SECONDARY = '#5b6470';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const BORDER_HOVER = '#d4d8df';
const SURFACE = '#ffffff';
const SURFACE_SUBTLE = '#f7f8fa';
const ACCENT = '#0f766e';
const ACCENT_TINT = 'rgba(15, 118, 110, 0.10)';
const DONE_ACCENT = '#10b981';
const DONE_FG = '#047857';
const DONE_BG = '#ecfdf5';
const DONE_TILE = '#d1fadf';
const PENDING_ACCENT = '#f59e0b';
const PENDING_FG = '#b45309';
const PENDING_BG = '#fff7ed';
const PENDING_TILE = '#fed7aa';
const ERROR = '#dc2626';
const CHIP_BLUE = '#1d4ed8';
const CHIP_BLUE_TINT = '#dbeafe';

const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';

const maskPrivate = (s: string): string => s.replace(/[a-z]/g, 'x');

const fullName = (p?: PersonDTO, showPersonalData = true): string => {
  if (!p) return '';
  const raw = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ');
  return showPersonalData ? raw : maskPrivate(raw);
};

const initials = (p?: PersonDTO): string => {
  if (!p) return '?';
  const first = (p.firstName?.[0] ?? p.name?.[0] ?? '').toUpperCase();
  const last = (p.lastName?.[0] ?? '').toUpperCase();
  return `${first}${last}` || '?';
};

const avatarColor = (p?: PersonDTO): string => {
  if (!p?.id) return '#dfe3ea';
  const palette = ['#e0e7ff', '#dcfce7', '#fef3c7', '#fce7f3', '#dbeafe', '#fee2e2', '#e6e8ec'];
  return palette[p.id % palette.length];
};

const departmentAndGroup = (orgUnit?: string): string | undefined => {
  if (!orgUnit) return undefined;
  return orgUnit.split('-').slice(0, 2).join('-');
};

const formatRelative = (d?: string | Date): string => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const ms = dt.getTime() - Date.now();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  try {
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    const months = Math.round(days / 30);
    if (Math.abs(months) >= 12) return rtf.format(Math.round(months / 12), 'year');
    if (Math.abs(days) >= 30) return rtf.format(months, 'month');
    return rtf.format(days, 'day');
  } catch {
    return '';
  }
};

const formatDateTime = (d?: string | Date): string => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const chipSx = {
  height: 20,
  borderRadius: 0.5,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: CHIP_BLUE,
  bgcolor: CHIP_BLUE_TINT,
  border: `1px solid ${CHIP_BLUE}`,
  '& .MuiChip-label': { px: 0.75 },
};

const inputFrameSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE,
    pr: 0.75,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: BORDER_HOVER },
    '&.Mui-focused fieldset': { borderColor: INK, borderWidth: 1 },
  },
};

const actionIconSx = {
  p: 0.5,
  color: TEXT_MUTED,
  transition: 'color 120ms ease, background-color 120ms ease',
  '&:hover': { color: ACCENT, bgcolor: ACCENT_TINT },
  '&:focus-visible': { outline: `1px solid ${ACCENT}`, outlineOffset: 1 },
};

const removeCardIconSx = {
  p: 0.5,
  color: TEXT_MUTED,
  transition: 'color 120ms ease, background-color 120ms ease',
  '&:hover': { color: ERROR, bgcolor: 'rgba(220, 38, 38, 0.10)' },
  '&:focus-visible': { outline: `1px solid ${ERROR}`, outlineOffset: 1 },
};

const ActionCard = ({
  onRemove,
  actionContent,
  setActionCardContent,
  initialData,
  disabled,
  index,
  showPersonalData = false,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const currentUser = initialData?.currentUser as unknown as PersonDTO | undefined;
  const isFetchingAction = useIsFetching([QueryKey.ACTION]);

  const isActionCompleted: boolean =
    actionContent.completionDte != undefined && actionContent.completionDte.length > 0;

  const ACTION_BASE_KEY = `actions[${index}]`;
  const ACTION_DESCRIPTION_KEY = ACTION_BASE_KEY + '.description';
  const ACTION_RESOLVER_KEY = ACTION_BASE_KEY + '.assignedUser';
  const ACTION_DATE_KEY = ACTION_BASE_KEY + (isActionCompleted ? '.assignedDte' : '.deadlineDte');

  // — Description (kept on react-hook-form's register so validation messages
  // continue to surface alongside the rest of the activity form).
  const descriptionRegister = register(ACTION_DESCRIPTION_KEY, {
    required: t(`activity.form.error.description.required`),
    minLength: { value: 5, message: t('activity.form.error.field.min.val.5') },
    maxLength: { value: 1000, message: t('activity.form.error.field.max.val.1000') },
  });

  useEffect(() => {
    if (actionContent.description) {
      setValue(ACTION_DESCRIPTION_KEY, actionContent.description);
    }
  }, [isFetchingAction]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (setActionCardContent) {
      setActionCardContent({ ...actionContent, description: e.target.value });
    }
    descriptionRegister.onChange(e);
  };

  // — Resolver picker (MUI Autocomplete, generic person search).
  const resolverRegister = register(ACTION_RESOLVER_KEY);
  const [resolverInput, setResolverInput] = useState('');
  const [resolverOptions, setResolverOptions] = useState<PersonDTO[]>([]);
  const [resolverLoading, setResolverLoading] = useState(false);

  useEffect(() => {
    setValue(ACTION_RESOLVER_KEY, actionContent.assignedUser);
  }, [actionContent.assignedUser]);

  const fetchResolverOptions = useMemo(
    () =>
      debounce((query: string, done: (results: PersonDTO[]) => void) => {
        if (!query || query.trim().length < 2) {
          done([]);
          return;
        }
        getPersonAutocomplete(query)
          .then(done)
          .catch(() => done([]));
      }, 250),
    []
  );

  useEffect(() => {
    setResolverLoading(true);
    fetchResolverOptions(resolverInput, results => {
      setResolverOptions(results);
      setResolverLoading(false);
    });
    return () => {
      fetchResolverOptions.cancel();
    };
  }, [resolverInput, fetchResolverOptions]);

  const setResolver = (next?: PersonDTO) => {
    if (setActionCardContent) {
      setActionCardContent({ ...actionContent, assignedUser: next });
    }
  };

  const handleResolverChange = (_: unknown, value: PersonDTO | null) => {
    setResolver(value || undefined);
  };

  const pickMeResolver = () => {
    if (currentUser) setResolver(currentUser);
  };

  const clearResolver = () => setResolver(undefined);

  const resolverOptionsList = actionContent.assignedUser
    ? [
        actionContent.assignedUser,
        ...resolverOptions.filter(o => o.id !== actionContent.assignedUser!.id),
      ]
    : resolverOptions;

  const selectedResolverChip = departmentAndGroup(actionContent.assignedUser?.orgUnit);
  const canPickMe = !!currentUser && !actionContent.assignedUser;

  // — Date picker (deadline or completion, depending on completion state).
  const dateValue = isActionCompleted ? actionContent.completionDte : actionContent.deadlineDte;
  const dateRegister = register(ACTION_DATE_KEY);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    setValue(ACTION_DATE_KEY, dateValue);
  }, [dateValue]);

  const setDate = (next?: string) => {
    if (!setActionCardContent) return;
    if (isActionCompleted) {
      setActionCardContent({ ...actionContent, completionDte: next });
    } else {
      // deadlineDte is typed as a required string on the DTO — use empty
      // string for "cleared" rather than undefined to satisfy the contract.
      setActionCardContent({ ...actionContent, deadlineDte: next ?? '' });
    }
  };

  const handleDateChange = (next: Moment | null) => {
    setDate(next && next.isValid() ? next.format(backendDateFormat) : undefined);
  };

  const pickToday = () => setDate(moment().format(backendDateFormat));

  const clearDate = () => setDate(undefined);

  // — Comments (inline composer + list, mirroring ActionDetailView).
  const sortedComments = (actionContent.comments ?? [])
    .slice()
    .sort((a, b) => new Date(b.createDte).getTime() - new Date(a.createDte).getTime());

  const postCommentFn = usePostComment(actionContent.id || 0);
  const [commentText, setCommentText] = useState('');
  const [commentFocused, setCommentFocused] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const canComment = !!actionContent.id;

  const handlePostComment = () => {
    const description = commentText.trim();
    if (!description || isPosting || !canComment) return;
    setIsPosting(true);
    const newComment: ActionCommentDTO = {
      description,
      createDte: Date.now().toString(),
    };
    postCommentFn(newComment)
      .then(action => {
        setActionCardContent?.(action);
        setCommentText('');
      })
      .catch(() => undefined)
      .finally(() => setIsPosting(false));
  };

  // — Index label: prefer the saved id, fall back to "NEW" for unsaved cards.
  const idLabel = actionContent.id
    ? `#${actionContent.id}`
    : t('action.form.indexNew').toUpperCase();
  const indexLabel = `${t('action.form.indexLabel')} ${idLabel}`;

  // Saved actions have an individual detail page — reachable in-app and
  // shareable as an absolute link.
  const actionRoute =
    actionContent.id != null
      ? Action.children.actionDetails.route(String(actionContent.id))
      : undefined;
  const actionUrl = actionRoute ? `${window.location.origin}${actionRoute}` : undefined;

  const descriptionError = lodash.get(errors, ACTION_DESCRIPTION_KEY)?.message?.toString();

  // Status palette — completed and pending each get their own stripe + pill so
  // a stack of action cards is scannable for state at a glance.
  const status = isActionCompleted
    ? {
        accent: DONE_ACCENT,
        fg: DONE_FG,
        bg: DONE_BG,
        tile: DONE_TILE,
        label: t('action.label.completed'),
      }
    : {
        accent: PENDING_ACCENT,
        fg: PENDING_FG,
        bg: PENDING_BG,
        tile: PENDING_TILE,
        label: t('action.label.pending'),
      };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: BORDER,
        borderRadius: '10px',
        bgcolor: SURFACE,
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:hover': {
          borderColor: BORDER_HOVER,
          boxShadow: '0 1px 0 rgba(17, 20, 24, 0.04)',
        },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bgcolor: status.accent,
        }}
      />

      {/* — Header: index + status pill + remove */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          pl: 2.25,
          pr: 1,
          py: 1,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: TEXT_MUTED,
              flexShrink: 0,
            }}
          >
            {indexLabel}
          </Typography>
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              height: 22,
              px: 0.875,
              borderRadius: '5px',
              border: `1px solid ${status.tile}`,
              bgcolor: status.bg,
              flexShrink: 0,
            }}
          >
            {isActionCompleted ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 12, color: status.accent }} />
            ) : (
              <HourglassEmptyRoundedIcon sx={{ fontSize: 12, color: status.accent }} />
            )}
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: status.fg,
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
              }}
            >
              {status.label}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {actionRoute && (
            <Tooltip title={t('action.form.openAction')}>
              <IconButton
                size="small"
                onClick={() => history.push(actionRoute)}
                aria-label={t('action.form.openAction').toString()}
                sx={{
                  color: TEXT_MUTED,
                  '&:hover': { color: INK, bgcolor: SURFACE_SUBTLE },
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {actionUrl && <ShareButton compact url={actionUrl} />}
          {!disabled && (
            <Tooltip title={t('action.form.remove')}>
              <IconButton size="small" onClick={onRemove} sx={removeCardIconSx}>
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* — Body: description + resolver + date */}
      <Box sx={{ pl: 2.25, pr: 1.75, py: 1.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: TEXT_MUTED,
            mb: 0.75,
          }}
        >
          {t('action.form.details.page.container.action.required.container.label')}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${descriptionError ? ERROR : BORDER}`,
            borderRadius: '8px',
            bgcolor: SURFACE,
            transition: 'border-color 120ms ease',
            '&:focus-within': { borderColor: descriptionError ? ERROR : INK },
            px: 1.5,
            py: 1,
          }}
        >
          <GridColumnInputWrapper
            value={actionContent.description}
            rows={1}
            disabled={disabled}
            onChange={handleDescriptionChange}
            specialRequiredCondition
            render={() => (
              <TextArea
                register={descriptionRegister}
                disabled={disabled}
                value={actionContent.description}
                rows={3}
                onChange={handleDescriptionChange}
              />
            )}
          />
        </Box>
        {descriptionError && (
          <Typography sx={{ mt: 0.5, fontSize: 12, color: ERROR }}>{descriptionError}</Typography>
        )}

        {/* Resolver + deadline — side by side at md+, stacked on small screens */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{ mt: 1.75 }}
        >
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: TEXT_MUTED,
                mb: 0.75,
              }}
            >
              {t('action.form.details.page.container.action.required.specify.resolver')}
            </Typography>
            <Autocomplete<PersonDTO, false, false, false>
              value={actionContent.assignedUser ?? null}
              onChange={handleResolverChange}
              inputValue={resolverInput}
              onInputChange={(_, v) => setResolverInput(v)}
              options={resolverOptionsList}
              loading={resolverLoading}
              disabled={disabled}
              popupIcon={null}
              size="small"
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              getOptionLabel={p => fullName(p, showPersonalData)}
              filterOptions={x => x}
              noOptionsText={
                resolverInput.trim().length < 2
                  ? t('action.form.resolver.typeToSearch')
                  : t('action.form.resolver.noMatches')
              }
              slotProps={{
                paper: {
                  sx: { mt: 0.5, borderRadius: 1, border: `1px solid ${BORDER}` },
                },
              }}
              renderOption={(props, option) => {
                const chipLabel = departmentAndGroup(option.orgUnit);
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{ py: 0.5, px: 1.25, gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {fullName(option, showPersonalData)}
                    </Typography>
                    {chipLabel && <Chip label={chipLabel} size="small" sx={chipSx} />}
                  </Box>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t('action.form.resolver.placeholder')}
                  inputRef={resolverRegister.ref}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -0.25, gap: 0.75 }}>
                        {selectedResolverChip && (
                          <Chip label={selectedResolverChip} size="small" sx={chipSx} />
                        )}
                        {actionContent.assignedUser ? (
                          <Tooltip title={t('action.form.resolver.remove')}>
                            <span>
                              <IconButton
                                size="small"
                                onMouseDown={e => e.preventDefault()}
                                onClick={clearResolver}
                                disabled={disabled}
                                sx={actionIconSx}
                              >
                                <CloseRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : canPickMe ? (
                          <Tooltip title={t('action.form.resolver.me')}>
                            <span>
                              <IconButton
                                size="small"
                                onMouseDown={e => e.preventDefault()}
                                onClick={pickMeResolver}
                                disabled={disabled}
                                sx={actionIconSx}
                              >
                                <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  sx={inputFrameSx}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: TEXT_MUTED,
                mb: 0.75,
              }}
            >
              {isActionCompleted
                ? t('action.form.details.page.container.action.required.specify.date.of.completion')
                : t('action.form.details.page.container.action.required.specify.date.for.deadline')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format={DISPLAY_DATE_FORMAT}
                value={dateValue ? moment(dateValue, backendDateFormat) : null}
                onChange={handleDateChange}
                disabled={disabled}
                open={dateOpen}
                onOpen={() => setDateOpen(true)}
                onClose={() => setDateOpen(false)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: DISPLAY_DATE_FORMAT.toLowerCase(),
                    inputRef: dateRegister.ref,
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: -0.25, gap: 0.25 }}>
                          {dateValue ? (
                            <Tooltip title={t('action.form.resolver.remove')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={clearDate}
                                  disabled={disabled}
                                  sx={actionIconSx}
                                >
                                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('action.form.deadline.today')}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={pickToday}
                                  disabled={disabled}
                                  sx={actionIconSx}
                                >
                                  <TodayOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={t('action.form.deadline.openCalendar')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setDateOpen(true)}
                                disabled={disabled}
                                sx={actionIconSx}
                              >
                                <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                    sx: inputFrameSx,
                  },
                  popper: {
                    sx: {
                      '& .MuiPickersDay-root.Mui-selected': {
                        bgcolor: INK,
                        '&:hover': { bgcolor: '#000000' },
                      },
                      '& .MuiPickersDay-root:focus.Mui-selected': { bgcolor: INK },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Box>

      {/* — Comments: inline composer + list. Mirrors ActionDetailView.
          Header is a clickable toggle so a stack of action cards stays
          scannable; expand only the one you want to read or comment on. */}
      <Box sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: SURFACE_SUBTLE }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => setCommentsOpen(o => !o)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCommentsOpen(o => !o);
            }
          }}
          sx={{
            px: 1.75,
            py: 1,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 120ms ease',
            '&:hover': { bgcolor: '#f1f3f6' },
            '&:focus-visible': {
              outline: `1px solid ${INK}`,
              outlineOffset: -1,
            },
          }}
          aria-expanded={commentsOpen}
        >
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14, color: TEXT_MUTED }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: TEXT_MUTED,
            }}
          >
            {t('action.form.comments.title')}
          </Typography>
          {sortedComments.length > 0 && (
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED }}>
              · {sortedComments.length}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <ExpandMoreRoundedIcon
            sx={{
              fontSize: 18,
              color: TEXT_MUTED,
              transform: commentsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 180ms ease',
            }}
          />
        </Stack>

        <Collapse in={commentsOpen} timeout={220} unmountOnExit>
          <Box sx={{ px: 1.75, pb: 1.5 }}>
        {!disabled && (
          <Box
            sx={{
              border: `1px solid ${commentFocused ? INK : BORDER}`,
              borderRadius: '8px',
              bgcolor: SURFACE,
              transition: 'border-color 120ms ease',
              overflow: 'hidden',
              mb: sortedComments.length > 0 ? 1.25 : 0,
              opacity: canComment ? 1 : 0.65,
            }}
          >
            <TextField
              multiline
              minRows={2}
              maxRows={8}
              fullWidth
              placeholder={
                canComment
                  ? t('action.form.comments.placeholder')
                  : t('action.form.comments.saveFirst')
              }
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              disabled={!canComment || isPosting}
              onFocus={() => setCommentFocused(true)}
              onBlur={() => setCommentFocused(false)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: TEXT,
                  px: 1.5,
                  py: 1,
                  '& textarea::placeholder': { color: TEXT_MUTED, opacity: 1 },
                },
              }}
            />
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                px: 1,
                py: 0.625,
                borderTop: `1px solid ${BORDER}`,
                bgcolor: SURFACE_SUBTLE,
              }}
            >
              <Button
                onClick={handlePostComment}
                disabled={!commentText.trim() || isPosting || !canComment}
                disableElevation
                sx={{
                  bgcolor: INK,
                  color: '#ffffff',
                  fontSize: 12.5,
                  fontWeight: 500,
                  textTransform: 'none',
                  height: 26,
                  px: 1.25,
                  borderRadius: '6px',
                  border: `1px solid ${INK}`,
                  minWidth: 'auto',
                  '&:hover': { bgcolor: '#000000' },
                  '&:disabled': {
                    bgcolor: SURFACE,
                    color: TEXT_MUTED,
                    borderColor: BORDER,
                  },
                }}
              >
                {isPosting
                  ? t('action.form.comments.posting')
                  : t('action.form.comments.post')}
              </Button>
            </Stack>
          </Box>
        )}

        {sortedComments.length === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${BORDER}`,
              borderRadius: '8px',
              py: 1.75,
              px: 2,
              textAlign: 'center',
              bgcolor: SURFACE,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED }}>
              {t('action.form.comments.empty')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.875}>
            {sortedComments.map((c, idx) => {
              const relative = formatRelative(c.createDte);
              const absolute = formatDateTime(c.createDte);
              return (
                <Box
                  key={c.id ?? `c-${idx}`}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                    bgcolor: SURFACE,
                    px: 1.5,
                    py: 1,
                    transition: 'border-color 120ms ease, background-color 120ms ease',
                    '&:hover': { borderColor: BORDER_HOVER, bgcolor: SURFACE_SUBTLE },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: TEXT,
                        bgcolor: avatarColor(c.createUser),
                        border: `1px solid ${BORDER}`,
                        flexShrink: 0,
                        mt: '1px',
                      }}
                    >
                      {initials(c.createUser)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={0.875}
                        alignItems="baseline"
                        flexWrap="wrap"
                        sx={{ mb: 0.25 }}
                      >
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: TEXT }}>
                          {fullName(c.createUser, showPersonalData)}
                        </Typography>
                        {relative && (
                          <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
                            {relative}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          sx={{ fontSize: 11.5, color: TEXT_MUTED }}
                          title={absolute}
                        >
                          · {absolute}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: TEXT,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {c.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

const mapStateToProps = (state: any) => ({
  showPersonalData: state.dataPrivacy?.showPersonalData ?? false,
});

export default connect(mapStateToProps, null)(ActionCard);
